import RoutePageTitle from "components/RoutePageTitle";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { getMdmAuthKeyByEmail } from "utils/getMdmAuthKeyByEmail";

const UNBOUND_MACHINES_PATH = "/api/v1/mdm/machine-master/unbound-machines";
const VENUE_LIST_PATH = "/api/v1/venue/listv2";
const CREATE_MACHINE_MASTER_PATH = "/api/v1/mdm/machine-master/create";

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");
const stripGraphQLSuffix = (value) => value.replace(/\/GraphQLHandler$/, "");
const buildBackendBase = (graphqlEndpoint) =>
  trimTrailingSlash(stripGraphQLSuffix(graphqlEndpoint || ""));

const resolveBackendBase = (graphqlEndpoint) => {
  const base = buildBackendBase(graphqlEndpoint);
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  if (!base || base === "undefined" || base === "null") return fallback;
  try {
    new URL(base);
    return base;
  } catch (error) {
    return fallback || base;
  }
};

const buildApiUrl = (base, path, params = {}) => {
  const url = new URL(path, base);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, value);
  });
  return url.toString();
};

const firstFilled = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && value !== "");
  return found === undefined ? "" : found;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("zh-TW", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const normalizeMachine = (item) => ({
  machineRef: firstFilled(item?.machine_ref),
  status: firstFilled(item?.status),
  matchCount: Number(item?.match_count || 0),
  currentVenueId: firstFilled(item?.current_venue_id),
  currentVenueName: firstFilled(item?.current_venue_name),
  isMqttPlaceholder: Boolean(item?.is_mqtt_placeholder),
  hasLocalMdm: Boolean(item?.has_local_mdm),
  localMdmId: firstFilled(item?.local_mdm_id),
  topic: firstFilled(item?.topic),
  canCreate: Boolean(item?.can_create),
  message: firstFilled(item?.message),
  createdAt: firstFilled(item?.created_at),
  matchedThirdPartyIds: Array.isArray(item?.matched_third_party_ids)
    ? item.matched_third_party_ids
    : [],
});

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  return [];
};

const sectionCardSx = {
  overflow: "visible",
};

const actionButtonSx = {
  borderRadius: "10px",
  px: 3,
  py: 0.9,
  fontWeight: 700,
  minWidth: 120,
  color: "#ffffff",
};

const resetButtonSx = {
  ...actionButtonSx,
  borderColor: "#2f6fe4",
  color: "#2f6fe4",
  backgroundColor: "#ffffff",
  "&:hover": {
    borderColor: "#1f5fd4",
    backgroundColor: "rgba(47, 111, 228, 0.06)",
  },
  "&.Mui-disabled": {
    borderColor: "#b7c7e6",
    color: "#b7c7e6",
  },
};

function MachineBootstrapSetupBK() {
  const { userData } = useUserContext() || {};
  const email = userData?.email ? userData.email.trim().toLowerCase() : "";
  const mdmAuthKey = useMemo(() => getMdmAuthKeyByEmail(email), [email]);
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const apiBase = useMemo(() => resolveBackendBase(backendUrl), [backendUrl]);

  const [unboundMachines, setUnboundMachines] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [selectedMachineRefs, setSelectedMachineRefs] = useState([]);
  const [targetSiteId, setTargetSiteId] = useState("");
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const creatableMachineRefs = useMemo(
    () => unboundMachines.filter((item) => item.canCreate).map((item) => item.machineRef),
    [unboundMachines]
  );

  const allCreatableSelected =
    creatableMachineRefs.length > 0 &&
    creatableMachineRefs.every((machineRef) => selectedMachineRefs.includes(machineRef));

  const someCreatableSelected =
    creatableMachineRefs.some((machineRef) => selectedMachineRefs.includes(machineRef)) &&
    !allCreatableSelected;

  const selectedMachines = useMemo(
    () => unboundMachines.filter((item) => selectedMachineRefs.includes(item.machineRef)),
    [selectedMachineRefs, unboundMachines]
  );

  const selectedSite = useMemo(
    () => siteOptions.find((option) => option.value === targetSiteId) || null,
    [siteOptions, targetSiteId]
  );

  const fetchUnboundMachines = useCallback(async () => {
    if (!email) return;
    setLoadingMachines(true);
    setPageError("");
    try {
      const url = buildApiUrl(apiBase, UNBOUND_MACHINES_PATH, { email });
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.code !== 200) {
        throw new Error(json?.message || `查詢未綁定機台失敗 (${response.status})`);
      }
      const nextRows = Array.isArray(json?.data?.items)
        ? json.data.items.map(normalizeMachine)
        : [];
      setUnboundMachines(nextRows);
      setSelectedMachineRefs((prev) =>
        prev.filter((machineRefValue) =>
          nextRows.some((item) => item.machineRef === machineRefValue)
        )
      );
    } catch (error) {
      setPageError(error.message || "查詢未綁定機台失敗");
      setUnboundMachines([]);
      setSelectedMachineRefs([]);
    } finally {
      setLoadingMachines(false);
    }
  }, [apiBase, email, mdmAuthKey]);

  const fetchSiteOptions = useCallback(async () => {
    if (!email) return;
    setLoadingSites(true);
    try {
      const url = buildApiUrl(apiBase, VENUE_LIST_PATH, { email, status: "" });
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.code !== 200) {
        throw new Error(json?.message || `查詢場域清單失敗 (${response.status})`);
      }
      const payload = normalizeArray(json?.data ?? json);
      const options = payload
        .map((item) => {
          const venue = item?.venue || item || {};
          const brand = item?.brand || {};
          const targetSiteId = firstFilled(
            item?.target_site_id,
            item?.targetSiteId,
            venue?.target_site_id,
            venue?.targetSiteId,
            venue.id
          );
          const value = String(targetSiteId || "");
          const label = [venue.name, venue.city, brand.name].filter(Boolean).join(" / ");
          if (!value || !label) return null;
          return {
            value,
            label,
            venue,
            brand,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label, "zh-Hant"));
      setSiteOptions(options);
    } catch (error) {
      setPageError((prev) => prev || error.message || "查詢場域清單失敗");
      setSiteOptions([]);
    } finally {
      setLoadingSites(false);
    }
  }, [apiBase, email, mdmAuthKey]);

  useEffect(() => {
    if (!email) return;
    fetchUnboundMachines();
    fetchSiteOptions();
  }, [email, fetchSiteOptions, fetchUnboundMachines]);

  const handleToggleMachine = (machineRef, canCreate) => {
    if (!canCreate) return;
    setSelectedMachineRefs((prev) =>
      prev.includes(machineRef) ? prev.filter((item) => item !== machineRef) : [...prev, machineRef]
    );
  };

  const handleToggleSelectAll = () => {
    if (allCreatableSelected) {
      setSelectedMachineRefs([]);
      return;
    }
    setSelectedMachineRefs(creatableMachineRefs);
  };

  const handleReset = () => {
    setTargetSiteId("");
    setSelectedMachineRefs([]);
    setSubmitError("");
    setSuccessMessage("");
    setPageError("");
    fetchUnboundMachines();
  };

  const handleSubmit = async () => {
    if (!targetSiteId) {
      setSubmitError("請先選擇目標場域");
      return;
    }
    if (!selectedMachineRefs.length) {
      setSubmitError("請先選擇至少一台可建置機台");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    setSuccessMessage("");
    try {
      const response = await fetch(buildApiUrl(apiBase, CREATE_MACHINE_MASTER_PATH), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Mdm-Auth": mdmAuthKey,
        },
        body: JSON.stringify({
          email,
          target_site_id: targetSiteId,
          machine_refs: selectedMachineRefs,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.code !== 200) {
        throw new Error(json?.message || `建立正式主檔失敗 (${response.status})`);
      }
      const items = Array.isArray(json?.data?.items) ? json.data.items : [];
      const successCount = items.filter((item) =>
        ["created", "bound_existing", "already_bound"].includes(item?.status)
      ).length;
      setSuccessMessage(
        successCount === items.length
          ? `已完成 ${successCount} 台機台建置`
          : `已完成 ${successCount}/${items.length} 台機台建置`
      );
      setSelectedMachineRefs([]);
      await fetchUnboundMachines();
    } catch (error) {
      setSubmitError(error.message || "建立正式主檔失敗");
    } finally {
      setSubmitting(false);
    }
  };

  const unboundTable = useMemo(() => {
    const columns = [
      {
        Header: (
          <Checkbox
            checked={allCreatableSelected}
            indeterminate={someCreatableSelected}
            onChange={handleToggleSelectAll}
          />
        ),
        accessor: "select",
        width: "5%",
      },
      { Header: "機器編號", accessor: "machineRef" },
      { Header: "建立時間", accessor: "createdAt" },
    ];

    const rows = unboundMachines.map((item) => ({
      select: (
        <Checkbox
          checked={selectedMachineRefs.includes(item.machineRef)}
          disabled={!item.canCreate}
          onChange={() => handleToggleMachine(item.machineRef, item.canCreate)}
        />
      ),
      machineRef: item.machineRef || "-",
      createdAt: formatDateTime(item.createdAt),
    }));

    return { columns, rows };
  }, [
    allCreatableSelected,
    handleToggleMachine,
    selectedMachineRefs,
    someCreatableSelected,
    unboundMachines,
  ]);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={2.5}
            px={2.5}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <MDTypography variant="h6" color="white">
              <RoutePageTitle />
            </MDTypography>
          </MDBox>

          <MDBox pt={3} px={3} pb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={sectionCardSx}>
                  <MDBox p={3}>
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={1}
                      mb={2}
                    >
                      <div>
                        <MDTypography variant="h6">未綁定機台列表</MDTypography>
                        <MDTypography variant="button" color="text">
                          待處理機台 {unboundMachines.length} 台
                        </MDTypography>
                      </div>
                      <MDBox display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                        {loadingMachines ? <CircularProgress size={24} /> : null}
                        <Button
                          variant="outlined"
                          sx={resetButtonSx}
                          onClick={handleReset}
                          disabled={loadingMachines || submitting}
                        >
                          重新整理
                        </Button>
                      </MDBox>
                    </MDBox>
                    {pageError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {pageError}
                      </Alert>
                    ) : null}
                    {!loadingMachines && unboundMachines.length === 0 ? (
                      <Alert severity="info">目前沒有待建置機台。</Alert>
                    ) : (
                      <DataTable
                        table={unboundTable}
                        entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
                        canSearch
                        showTotalEntries
                        isSorted={false}
                        noEndBorder
                      />
                    )}
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={sectionCardSx}>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      建機設定
                    </MDTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        <FormControl
                          fullWidth
                          size="small"
                          disabled={loadingSites || !siteOptions.length}
                        >
                          <InputLabel id="target-site-label">目標場域</InputLabel>
                          <Select
                            labelId="target-site-label"
                            value={targetSiteId}
                            label="目標場域"
                            onChange={(event) => setTargetSiteId(event.target.value)}
                          >
                            {siteOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {loadingSites ? (
                          <MDTypography variant="caption" color="text">
                            載入場域清單中...
                          </MDTypography>
                        ) : null}
                        {!loadingSites && !siteOptions.length ? (
                          <MDTypography variant="caption" color="error">
                            目前權限範圍內無可綁定場域
                          </MDTypography>
                        ) : null}
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <MDBox px={2} py={1.5} borderRadius="12px" bgColor="#f8fafc" height="100%">
                          <MDTypography variant="button" color="text">
                            已選機台 {selectedMachines.length} 台
                          </MDTypography>
                          <MDBox mt={1} display="flex" gap={1} flexWrap="wrap">
                            {selectedMachines.length ? (
                              selectedMachines.map((item) => (
                                <Chip
                                  key={item.machineRef}
                                  label={item.machineRef}
                                  onDelete={() => handleToggleMachine(item.machineRef, true)}
                                />
                              ))
                            ) : (
                              <MDTypography variant="caption" color="text">
                                尚未選擇機台
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                      </Grid>
                    </Grid>

                    <MDBox mt={2}>
                      {submitError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {submitError}
                        </Alert>
                      ) : null}
                      {successMessage ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {successMessage}
                        </Alert>
                      ) : null}
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1.5}
                      >
                        <div>
                          <MDTypography variant="button" color="text">
                            目標場域：{selectedSite?.label || "-"}
                          </MDTypography>
                        </div>
                        <Button
                          variant="contained"
                          sx={actionButtonSx}
                          disabled={
                            submitting ||
                            !selectedMachineRefs.length ||
                            !targetSiteId ||
                            !siteOptions.length
                          }
                          onClick={handleSubmit}
                        >
                          {submitting ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            "建立正式主檔"
                          )}
                        </Button>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default MachineBootstrapSetupBK;
